import { Injectable } from '@angular/core';

export type ChartYAxisMode = 'auto-data' | 'auto-bands' | 'manual';

export interface ChartOptionsConfig {
  yAxisMode: ChartYAxisMode;
  yMin?: number;
  yMax?: number;
  /** Relative-time X axis ("same origin" — elapsed time from each series'
   *  own start instead of absolute timestamps). Only meaningful when the
   *  host chart's sameOriginAllowed input is true. */
  sameOrigin?: boolean;
}

const STORAGE_KEY_PREFIX = 'chart.options.';

/**
 * Per-chart display preferences (Y-axis mode/range, X-axis same-origin),
 * keyed by the caller-supplied chartId (e.g. "shm_icorr") and persisted in
 * localStorage so they survive across sessions. Shared by any chart
 * component that opts in via ChartOptionsButtonComponent — not SHM-specific.
 */
@Injectable({ providedIn: 'root' })
export class ChartOptionsStorageService {
  get(chartId: string): ChartOptionsConfig | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_PREFIX + chartId);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object' || !parsed.yAxisMode) return null;
      return parsed as ChartOptionsConfig;
    } catch {
      return null;
    }
  }

  set(chartId: string, config: ChartOptionsConfig): void {
    try {
      localStorage.setItem(STORAGE_KEY_PREFIX + chartId, JSON.stringify(config));
    } catch {
      // Private window / storage disabled / quota exceeded — non-fatal, the
      // chart still works, the preference just won't persist.
    }
  }
}
